// src/components/admin/mail/MailMessageList.tsx
import React from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import { LoadStatus, MailMessageSummary } from '../../../types/mail';
import MailEmptyState from './MailEmptyState';
import MailMessageRow from './MailMessageRow';
import { messageKey } from './mailUtils';
import {
    IconButton,
    Input,
    PageButton,
    Pagination,
    PaneHeader,
    PaneTitle,
    ScrollArea,
    SearchWrapper,
    SubtleText,
} from './mail.styles';

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
`;

const Spinning = styled(RefreshCw)<{ $spinning: boolean }>`
    animation: ${(p) => (p.$spinning ? 'mail-spin 0.9s linear infinite' : 'none')};
    @keyframes mail-spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const PageInfo = styled.span`
    font-size: 0.8rem;
    color: #64748b;
    font-weight: 600;
`;

const SearchBar = styled.div`
    padding: 0.75rem 1.25rem;
    border-bottom: 1px solid #e2e8f0;
    box-sizing: border-box;
`;

interface Props {
    accountId: string;
    folderLabel: string;
    messages: MailMessageSummary[];
    listStatus: LoadStatus;
    selectedUid: number | null;
    uidValidity: number;
    accent: string;
    page: number;
    totalPages: number;
    totalElements: number;
    search: string;
    isSearching: boolean;
    isRefreshing: boolean;
    lastSyncedAt: Date | null;
    onSearchChange: (value: string) => void;
    onOpen: (uid: number) => void;
    onPageChange: (page: number) => void;
    onRefresh: () => void;
}

const MailMessageList: React.FC<Props> = ({
    accountId,
    folderLabel,
    messages,
    listStatus,
    selectedUid,
    uidValidity,
    accent,
    page,
    totalPages,
    totalElements,
    search,
    isSearching,
    isRefreshing,
    lastSyncedAt,
    onSearchChange,
    onOpen,
    onPageChange,
    onRefresh,
}) => {
    const syncedLabel = lastSyncedAt
        ? `Naposledy načítané o ${lastSyncedAt.toLocaleTimeString('sk-SK', {
              hour: '2-digit',
              minute: '2-digit',
          })}`
        : '';

    return (
        <>
            <PaneHeader>
                <HeaderLeft>
                    <PaneTitle>{folderLabel}</PaneTitle>
                    <SubtleText>
                        {totalElements > 0 ? `${totalElements} správ · ${syncedLabel}` : syncedLabel}
                    </SubtleText>
                </HeaderLeft>
                <IconButton
                    type="button"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    title="Aktualizovať"
                    aria-label="Aktualizovať"
                >
                    <Spinning size={16} $spinning={isRefreshing} />
                </IconButton>
            </PaneHeader>

            <SearchBar>
                <SearchWrapper>
                    <Search size={15} className="search-icon" />
                    <Input
                        type="search"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        /* "in this folder", not "in mail" — IMAP SEARCH is per-folder, and
                           promising global search would be a promise the backend can't keep. */
                        placeholder="Hľadať v priečinku…"
                        aria-label="Hľadať v priečinku"
                    />
                    {search && (
                        <button
                            type="button"
                            className="clear"
                            onClick={() => onSearchChange('')}
                            aria-label="Zrušiť hľadanie"
                        >
                            <X size={15} />
                        </button>
                    )}
                </SearchWrapper>
            </SearchBar>

            <ScrollArea>
                {listStatus === 'loading' && <MailEmptyState variant="listLoading" />}
                {listStatus === 'error' && <MailEmptyState variant="listError" onRetry={onRefresh} />}
                {listStatus === 'ready' && messages.length === 0 && (
                    <MailEmptyState
                        variant={isSearching ? 'emptySearch' : 'empty'}
                        onClearSearch={() => onSearchChange('')}
                    />
                )}
                {listStatus === 'ready' &&
                    messages.map((message) => (
                        <MailMessageRow
                            key={messageKey(accountId, uidValidity, message.uid)}
                            message={message}
                            selected={selectedUid === message.uid}
                            accent={accent}
                            onOpen={onOpen}
                        />
                    ))}
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination>
                    <PageButton
                        type="button"
                        onClick={() => onPageChange(page - 1)}
                        disabled={page <= 0}
                        aria-label="Predchádzajúca strana"
                    >
                        <ChevronLeft size={15} />
                    </PageButton>
                    <PageInfo>
                        Strana {page + 1} z {totalPages}
                    </PageInfo>
                    <PageButton
                        type="button"
                        onClick={() => onPageChange(page + 1)}
                        disabled={page >= totalPages - 1}
                        aria-label="Ďalšia strana"
                    >
                        <ChevronRight size={15} />
                    </PageButton>
                </Pagination>
            )}
        </>
    );
};

export default MailMessageList;
